import React, { useMemo, useState } from 'react';
import {
  Layout,
  Justify,
  Segment,
  Tabs,
  TabPanel,
} from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import CardCommon from '@op/components/CardCommon';
import {
  DEFAULT_REPORT_OWNER,
  ReportOwnerSegment,
  type TReportOwner,
} from '@op/components/ReportPermission';
import useTabHistory from '@op/hooks/useTabHistory';
import {
  TabType,
  useAiTaskGameList,
  useAiTaskTabInfo,
  REPEAT_SUMMARY_TASK,
} from './hooks';
import Filter from './components/Filter';
import CardLoading from '@op/components/CardLoading';
import SubscriptionsSummaryListTable from './components/SubscriptionsSummaryListTable';
import './index.scss';
import {
  buriedBtnPoint,
  useInitBuriedPointInfo,
} from '@op/operationLog/buriedPoint';
import { AITaskCenterPoint } from '@op/operationLog/pages';
import SummaryListTable from './components/SummaryListTable';

const { Content } = Layout;

export default function AITaskCenter({ }: any) {
  const { formatMessage } = useLanguage();
  const { tab, onActive } = useTabHistory<TabType>({ defaultValue: 'all', removeKeys: ["subtitle"] });
  const { tab: owner, onActive: onOwnerActive } = useTabHistory<TReportOwner>({
    key: 'owner',
    defaultValue: DEFAULT_REPORT_OWNER,
  });

  useInitBuriedPointInfo({
    buriedPoint: AITaskCenterPoint,
  });

  const {
    gameList,
    gameInfoMap,
    loading: gameListLoading,
  } = useAiTaskGameList({});

  const [filter, setFilter] = useState({
    search_text: '',
    edition_unified_ids: [],
    owner,
  });

  const handleOwnerChange = (value: TReportOwner) => {
    buriedBtnPoint('ReportOwner', value);
    onOwnerActive({ id: value });
    onActive({ id: 'all' });
    setFilter((prev) => ({ ...prev, owner: value }));
  };

  const { loading, segmentOptions, deleteToggleState, GENERATE_REPEAT_REPORT } = useAiTaskTabInfo({
    filter,
  });

  return (
    <Content className="dbo-page dbo-ai-task-center-wrapper">
      <Content.Header
        title={<>{formatMessage({ id: 'AI Task Center' })}</>}
        subtitle={
          <div className="dbo-header-subtitle">
            {formatMessage({
              id: 'All AI summary tasks generated on individual game pages will be displayed here',
            })}
          </div>
        }
      />
      <Content.Body full>
        <CardLoading loading={gameListLoading}>
          <Filter
            change={function (key: string, value: any): void {
              if (key === 'search_text') {
                if (value !== filter.search_text) {
                  setFilter({ ...filter, search_text: value });
                }
              } else {
                setFilter({ ...filter, [key]: value });
              }
            }}
            gameList={gameList}
            filter={filter}
          ></Filter>
          <CardCommon loading={loading} loadingHeight={615}>
            <Justify
              left={
                <div className="dbo-ai-task-center-segments">
                  <ReportOwnerSegment
                    value={owner}
                    onChange={(value: TReportOwner) => handleOwnerChange(value)}
                  />
                  <Segment
                    className="segment-font12"
                    value={tab}
                    onChange={(value: TabType) => {
                      buriedBtnPoint('SummaryType', undefined, value);
                      onActive({ id: value });
                    }}
                    options={segmentOptions}
                  />
                </div>
              }
            />
            <Tabs
              className="dbo-hide-tabs"
              tabs={segmentOptions}
              activeId={tab}
              style={{ marginTop: 10 }}
              destroyInactiveTabPanel={false}
            >
              {segmentOptions.map((item) => {
                const { value, keys, total, empty, children } = item;
                return (
                  <TabPanel key={value} id={value}>
                    <CardCommon
                      contentCard
                      emptyHeight={615}
                      empty={total === 0}
                      {...empty}
                    >
                      {value === REPEAT_SUMMARY_TASK ? (
                        <SubscriptionsSummaryListTable
                          filter={filter}
                          GENERATE_REPEAT_REPORT={GENERATE_REPEAT_REPORT}
                          keys={keys}
                          tabs={children}
                          defaultTab={children?.[0]?.value}
                          deleteToggleState={deleteToggleState}
                          gameInfoMap={gameInfoMap}
                        ></SubscriptionsSummaryListTable>
                      ) : (
                        <SummaryListTable
                          filter={filter}
                          source={value}
                          keys={keys}
                          deleteToggleState={deleteToggleState}
                          gameInfoMap={gameInfoMap}
                          urlTab={value}
                        ></SummaryListTable>
                      )}
                    </CardCommon>
                  </TabPanel>
                );
              })}
            </Tabs>
          </CardCommon>
        </CardLoading>
      </Content.Body>
    </Content>
  );
}


